import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    pollUrl: String,
    interval: { type: Number, default: 1500 }
  }

  connect() {
    this.poll()
    this.startPolling()
    // Delay dismiss to ensure toast has been created
    setTimeout(() => this.dismissImportStartedFlash(), 100)
    // Also dismiss on subsequent polls in case toast appears later
    this.dismissAttempts = 0
    this.consecutiveErrors = 0
    this.maxConsecutiveErrors = 5
  }

  disconnect() {
    this.stopPolling()
  }

  startPolling() {
    this.pollTimer = setInterval(() => this.poll(), this.intervalValue)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  dismissImportStartedFlash() {
    const toasts = document.querySelectorAll(".toast")
    let dismissed = false
    toasts.forEach(toast => {
      if (toast.textContent.includes("Import started")) {
        toast.remove()
        dismissed = true
      }
    })

    // Retry a few times in case toast is created after first attempt
    if (!dismissed && this.dismissAttempts < 5) {
      this.dismissAttempts++
      setTimeout(() => this.dismissImportStartedFlash(), 100)
    }
  }

  reloadWithoutImportId() {
    this.stopPolling()
    const url = new URL(window.location.href)
    url.searchParams.delete("import_id")
    // Use Turbo visit for cleaner navigation, fallback to location change
    if (window.Turbo) {
      window.Turbo.visit(url.toString(), { action: "replace" })
    } else {
      window.location.replace(url.toString())
    }
  }

  async poll() {
    if (!this.pollUrlValue) return

    try {
      const response = await fetch(this.pollUrlValue, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        this.consecutiveErrors = 0
        const html = await response.text()
        if (html.trim()) {
          const target = this.element.querySelector("#import-progress")
          if (target) {
            target.outerHTML = html
          }

          const isCompleted = html.includes("border-emerald-200")
          const isError = html.includes("border-red-200")

          if (isCompleted) {
            this.stopPolling()
            // Show brief success before reloading
            setTimeout(() => this.reloadWithoutImportId(), 1500)
          } else if (isError) {
            this.stopPolling()
          }
        }
      } else {
        this.handlePollError()
      }
    } catch (e) {
      console.debug("Import poll error:", e)
      this.handlePollError()
    }
  }

  handlePollError() {
    this.consecutiveErrors++

    if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
      this.stopPolling()
      this.showConnectionError()
    }
  }

  showConnectionError() {
    const target = this.element.querySelector("#import-progress")
    if (target) {
      target.innerHTML = `
        <div class="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
            <div>
              <p class="font-medium text-amber-800">Connection interrupted</p>
              <p class="text-sm text-amber-700 mt-1">The import may still be running in the background. Refresh the page to check status.</p>
              <button type="button" onclick="location.reload()" class="mt-3 text-sm font-medium text-amber-700 hover:text-amber-900 underline">
                Refresh page
              </button>
            </div>
          </div>
        </div>
      `
    }
  }
}
